// Generate dining recommendations near an event location
// Using Yelp Fusion API for proximity-based results

export interface DiningOption {
  name: string;
  cuisine: string;
  searchUrl: string;
  description: string;
  rating?: number;
  reviewCount?: number;
  price?: string;
  distance?: number;
  address?: string;
  phone?: string;
  image?: string;
}

// Sample popular restaurants in Houston metro area by city
const HOUSTON_DINING: Record<string, DiningOption[]> = {
  'Houston': [
    {
      name: "The Original Ninfa's on Navigation",
      cuisine: 'Mexican',
      searchUrl: 'https://www.opentable.com/r/ninfas-on-navigation-houston',
      description: 'Family-friendly Tex-Mex with famous fajitas'
    },
    {
      name: "Barnaby's Cafe",
      cuisine: 'American',
      searchUrl: 'https://www.opentable.com/r/barnabys-cafe-montrose-houston',
      description: 'Casual cafe with kid-friendly menu'
    },
    {
      name: "Chuy's",
      cuisine: 'Tex-Mex',
      searchUrl: 'https://www.opentable.com/r/chuys-houston-westheimer',
      description: 'Fun atmosphere with kids menu'
    }
  ],
  'Sugar Land': [
    {
      name: 'The Rouxpour',
      cuisine: 'American',
      searchUrl: 'https://www.opentable.com/r/the-rouxpour-sugar-land',
      description: 'Louisiana-inspired cuisine, family-friendly'
    },
    {
      name: "Perry's Steakhouse",
      cuisine: 'Steakhouse',
      searchUrl: 'https://www.opentable.com/r/perrys-steakhouse-and-grille-sugar-land',
      description: 'Upscale with kids menu available'
    },
    {
      name: "BJ's Restaurant",
      cuisine: 'American',
      searchUrl: 'https://www.opentable.com/r/bjs-restaurant-and-brewhouse-sugar-land',
      description: 'Casual dining with extensive kids menu'
    }
  ],
  'Katy': [
    {
      name: 'Black Walnut Cafe',
      cuisine: 'American',
      searchUrl: 'https://www.opentable.com/r/black-walnut-cafe-katy',
      description: 'All-day breakfast and lunch, kid-friendly'
    },
    {
      name: "Pappasito's Cantina",
      cuisine: 'Mexican',
      searchUrl: 'https://www.opentable.com/r/pappasitos-cantina-katy',
      description: 'Popular Tex-Mex chain, great for families'
    },
    {
      name: 'Local Foods',
      cuisine: 'American',
      searchUrl: 'https://www.opentable.com/r/local-foods-katy',
      description: 'Fresh, healthy options for all ages'
    }
  ],
  'The Woodlands': [
    {
      name: 'Corkscrew BBQ',
      cuisine: 'BBQ',
      searchUrl: 'https://corkscrewbbq.com',
      description: 'Award-winning BBQ, casual family dining'
    },
    {
      name: "Grimaldi's Pizzeria",
      cuisine: 'Italian',
      searchUrl: 'https://www.opentable.com/r/grimaldis-pizzeria-the-woodlands',
      description: 'Coal-fired pizza, great for kids'
    },
    {
      name: 'The Toasted Yolk',
      cuisine: 'Breakfast',
      searchUrl: 'https://www.opentable.com/r/the-toasted-yolk-the-woodlands',
      description: 'All-day breakfast with kids favorites'
    }
  ]
};

// Fallback restaurants for cities not in our database
const DEFAULT_DINING: DiningOption[] = [
  {
    name: 'Search Nearby Restaurants',
    cuisine: 'Various',
    searchUrl: '',
    description: 'Find restaurants near this event'
  }
];

// Fetch nearby restaurants using Google Places API
export async function getDiningOptions(
  city: string,
  latitude?: number,
  longitude?: number
): Promise<DiningOption[]> {
  // If we have coordinates, fetch real restaurants from Google Places API
  if (latitude && longitude) {
    try {
      const response = await fetch(`/api/places-nearby?latitude=${latitude}&longitude=${longitude}&limit=20`);
      if (response.ok) {
        const data = await response.json();
        if (data.restaurants && data.restaurants.length > 0) {
          // Map restaurants with ratings
          const restaurants = data.restaurants.map((r: any) => ({
            name: r.name,
            cuisine: r.cuisine || 'Restaurant',
            searchUrl: r.url,
            description: `${r.rating ? `⭐ ${r.rating} (${r.reviewCount} reviews)` : ''} ${r.price || ''}`.trim() || 'Great dining option nearby',
            rating: r.rating,
            reviewCount: r.reviewCount,
            price: r.price,
            address: r.address,
            phone: r.phone,
            image: r.image,
          }));
          
          // Sort by rating × reviews (descending) and return top 3
          const sorted = restaurants.sort((a: DiningOption, b: DiningOption) => {
            const scoreA = (a.rating || 0) * (a.reviewCount || 0);
            const scoreB = (b.rating || 0) * (b.reviewCount || 0);
            return scoreB - scoreA; // Highest score first
          });
          
          return sorted.slice(0, 3); // Top 3 only
        }
      }
    } catch (error) {
      console.error('Error fetching Google Places restaurants:', error);
      // Fall through to static recommendations
    }
  }

  // Fallback to static recommendations if API fails or no coordinates
  const cityOptions = HOUSTON_DINING[city];
  
  if (cityOptions) {
    return cityOptions;
  }

  // Final fallback with Google Maps search links
  if (latitude && longitude) {
    return [
      {
        name: 'Family Restaurants',
        cuisine: 'Family-Friendly',
        searchUrl: `https://www.google.com/maps/search/family+restaurants/@${latitude},${longitude},15z`,
        description: 'Find family-friendly restaurants nearby',
      },
      {
        name: 'Pizza Places',
        cuisine: 'Pizza',
        searchUrl: `https://www.google.com/maps/search/pizza/@${latitude},${longitude},15z`,
        description: 'Find pizza places nearby',
      },
      {
        name: 'Mexican Food',
        cuisine: 'Mexican',
        searchUrl: `https://www.google.com/maps/search/mexican+restaurants/@${latitude},${longitude},15z`,
        description: 'Find Mexican restaurants nearby',
      }
    ];
  }

  return DEFAULT_DINING;
}


// Generate OpenTable search URL for a specific location
export function getOpenTableSearchUrl(latitude: number, longitude: number, cuisine?: string): string {
  const term = cuisine ? `&term=${encodeURIComponent(cuisine)}` : '';
  return `https://www.opentable.com/s/?covers=2&latitude=${latitude}&longitude=${longitude}${term}`;
}
